import { X, Search, MapPin, Loader2 } from 'lucide-react';
import { forwardRef } from 'react';
import { Button } from '../../ui/button';

const SearchHeader = forwardRef(function SearchHeader({
  searchQuery,
  setSearchQuery,
  isSearching,
  showSearchResults,
  searchResults,
  isGooglePlacesAvailable,
  onClose,
  onSelectResult,
  onFallbackSearch
}, ref) {
  return (
    <div
      className="flex-shrink-0 bg-white border-b border-stone-200 p-4 space-y-3"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', position: 'relative', zIndex: 20 }}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-stone-100 active:bg-stone-200 transition-colors"
          data-testid="close-address-picker"
          style={{ touchAction: 'manipulation' }}
        >
          <X className="w-6 h-6" />
        </button>
        <h2 className="font-semibold text-lg flex-1">Set Location</h2>
      </div>

      {/* Search bar - Google Places Autocomplete or Fallback */}
      <div className="relative" style={{ zIndex: 1000 }}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 pointer-events-none z-10" />
        {isGooglePlacesAvailable ? (
          <input
            ref={ref}
            type="text"
            placeholder="Search places, addresses, landmarks..."
            defaultValue={searchQuery}
            className="w-full pl-10 pr-4 h-12 bg-stone-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="location-search-input"
            autoComplete="off"
          />
        ) : (
          <input
            ref={ref}
            type="text"
            placeholder="Search for a location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onFallbackSearch(); }}
            className="w-full pl-10 pr-4 h-12 bg-stone-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="location-search-input"
          />
        )}
        {!isGooglePlacesAvailable && (
          <Button
            size="sm"
            onClick={onFallbackSearch}
            disabled={isSearching || !searchQuery.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8"
          >
            {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </Button>
        )}
      </div>

      {isGooglePlacesAvailable && (
        <p className="text-xs text-stone-500 flex items-center gap-1">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          Powered by Google Places - Start typing for suggestions
        </p>
      )}

      {/* Fallback search results dropdown */}
      {!isGooglePlacesAvailable && showSearchResults && searchResults.length > 0 && (
        <div className="absolute left-4 right-4 top-32 bg-white rounded-xl shadow-xl border border-stone-200 max-h-60 overflow-y-auto z-20">
          {searchResults.map((result) => (
            <button
              key={result.place_id || result.osm_id || `search-${result.display_name}`}
              onClick={() => onSelectResult(result)}
              className="w-full px-4 py-3 text-left hover:bg-stone-50 border-b border-stone-100 last:border-0"
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">{result.display_name?.split(',')[0]}</p>
                  <p className="text-xs text-stone-500 line-clamp-2">{result.display_name}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default SearchHeader;
